import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { CreatePlatformDTO, PlatformResponseDTO, UpdatePlatformDTO } from '@libs/dtos/platform/platform.dto';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { PlatformService } from '@services/platforms/platform.service';
import { ValidationService } from '@services/ui/validation.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';
import { catchError, finalize, of } from 'rxjs';

@Component({
	selector: 'app-platform-crud',
	imports: [SharedModule, ButtonComponent, InputComponent],
	templateUrl: './platform-crud.component.html',
	styleUrl: './platform-crud.component.scss',
})
export class PlatformCrudComponent {
	/** Emitted only on a successful save, with the saved platform */
	@Output() platformUpdated = new EventEmitter<{ platform: PlatformResponseDTO; created: boolean }>();

	visible = false;
	isEditing = false;
	currentPlatformId: string | null = null;
	dialogTitle = 'Nueva Plataforma';
	actionButtonLabel = 'Añadir Plataforma';
	isLoading = false;
	platformForm: FormGroup;

	constructor(
		private fb: FormBuilder,
		private platformService: PlatformService,
		private validationService: ValidationService,
		private messageAlertService: MessageAlertService,
		private translocoService: TranslocoService,
	) {
		this.platformForm = this.fb.group({
			name: ['', [Validators.required]],
			link: ['', [Validators.pattern('https?://.*')]],
		});
	}

	/**
	 * Opens the modal for creating or editing a platform.
	 */
	show(platform?: PlatformResponseDTO): void {
		this.visible = true;
		this.isEditing = !!platform;
		this.currentPlatformId = platform?.id?.toString() || null;
		this.dialogTitle = this.translocoService.translate(
			this.isEditing ? 'platformsCrud.dialogTitleFull.edit' : 'platformsCrud.dialogTitleFull.new',
		);
		this.actionButtonLabel = this.translocoService.translate(
			this.isEditing ? 'platformsCrud.actionButton.edit' : 'platformsCrud.actionButton.new',
		);

		if (this.isEditing && platform) {
			this.platformForm.patchValue({
				name: platform.name,
				link: platform.link,
			});
		} else {
			this.platformForm.reset();
		}
	}

	/**
	 * Handles form submission for creating or updating a platform.
	 */
	onSubmit(): void {
		if (this.isLoading) return;

		if (this.platformForm.invalid) {
			this.platformForm.markAllAsTouched();
			return;
		}

		this.isLoading = true;
		const formData = this.platformForm.value;

		const request$ =
			this.isEditing && this.currentPlatformId
				? this.platformService.updatePlatform(this.currentPlatformId, formData as UpdatePlatformDTO)
				: this.platformService.createPlatform(formData as CreatePlatformDTO);

		// Keep the dialog open until the server answers: on error the typed values stay
		request$
			.pipe(
				catchError((err) => {
					// errors.<code> from the backend; generic key as fallback
					const errorMessage = translateApiError(
						err,
						this.translocoService,
						this.isEditing ? 'platformsCrud.messages.updateError' : 'platformsCrud.messages.createError',
					);

					this.messageAlertService.showMessage(
						'error',
						this.translocoService.translate('common.labels.error'),
						errorMessage,
					);

					return of(null);
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((response) => {
				if (response) {
					this.visible = false;
					this.platformUpdated.emit({ platform: response, created: !this.isEditing });
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						this.translocoService.translate(
							this.isEditing
								? 'platformsCrud.messages.updateSuccess'
								: 'platformsCrud.messages.createSuccess',
						),
					);
				}
			});
	}

	/**
	 * Retrieves validation error messages for a given form field.
	 */
	getErrorMessage(field: string): string | null {
		const control = this.platformForm?.get(field);
		if (control?.errors) {
			const errorKey = Object.keys(control.errors)[0];
			return this.validationService.getValidatorErrorMessage(errorKey) || null;
		}
		return null;
	}

	/**
	 * Handles "Enter" key press to submit the form.
	 */
	submitOnEnter(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.onSubmit();
		}
	}

	submitOnEnterSafe(event: Event): void {
		this.submitOnEnter(event as KeyboardEvent);
	}
}
